import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Smartphone } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { staffAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useFacility } from '../../context/FacilityContext';
import { ROLE_NAMES } from '../../constants/modules';
import StaffTabs from './StaffTabs';
import { AttentionStrip, Button } from '../../components/ui';

const ROLE_OPTIONS = [
  { value: 'MANAGER', label: 'Manager' },
  { value: 'OPERATOR', label: 'Operator' },
  { value: 'GROUNDSMAN', label: 'Ground keeper' },
  { value: 'PITCH_CURATOR', label: 'Pitch curator' },
  { value: 'CLEANER', label: 'Cleaner' },
  { value: 'ELECTRICIAN', label: 'Electrician' },
];

const LOGIN_ROLE_OPTIONS = [
  { value: 'Staff', label: 'Staff (selfie + operations)' },
  { value: 'Manager', label: 'Manager (broader access)' },
];

function normalizePhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length === 10 ? digits : null;
}

export default function AddEditStaff() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeFacilityId } = useFacility();
  const errorRef = useRef(null);
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    role: 'GROUNDSMAN',
    joining_date: '',
    salary_type: 'MONTHLY',
    salary_amount: '',
    status: 'ACTIVE',
    grant_app_access: false,
    login_role_name: 'Staff',
  });

  const canGrantAppAccess = useMemo(
    () => (
      user?.role_name === ROLE_NAMES.SUPER_ADMIN
      || user?.role_name === ROLE_NAMES.ADMIN
      || user?.role_name === ROLE_NAMES.MANAGER
    ),
    [user?.role_name]
  );

  useEffect(() => {
    if (isEdit && id) {
      staffAPI.getById(id).then((res) => {
        const d = res.data;
        setForm({
          name: d.name || '',
          phone: d.phone || '',
          email: d.email || '',
          role: d.role || 'GROUNDSMAN',
          joining_date: d.joining_date ? d.joining_date.slice(0, 10) : '',
          salary_type: d.salary_type || 'MONTHLY',
          salary_amount: d.salary_amount != null ? String(d.salary_amount) : '',
          status: d.status || 'ACTIVE',
          grant_app_access: Boolean(d.user_id),
          login_role_name: 'Staff',
        });
      }).catch(() => navigate('/dashboard/staff'));
    }
  }, [id, isEdit, navigate]);

  const showError = (message, phoneFieldError = '') => {
    setErrorMsg(message);
    setPhoneError(phoneFieldError);
    requestAnimationFrame(() => {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setPhoneError('');

    if (!activeFacilityId) {
      showError('Select a facility from the header before creating an employee.');
      return;
    }

    if (!form.name?.trim() || !form.role || !form.salary_type) {
      showError('Name, job role, and salary type are required.');
      return;
    }

    const normalizedPhone = normalizePhone(form.phone);
    if (!isEdit && form.grant_app_access && !normalizedPhone) {
      showError(
        'Enter a valid 10-digit mobile number to grant app access.',
        'Use a 10-digit Indian mobile number (no country code).'
      );
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: normalizedPhone || form.phone?.trim() || null,
        email: form.email?.trim() || null,
        role: form.role,
        joining_date: form.joining_date || null,
        salary_type: form.salary_type,
        salary_amount: parseFloat(form.salary_amount) || 0,
        status: form.status,
      };
      if (!isEdit && form.grant_app_access) {
        payload.grant_app_access = true;
        payload.login_role_name = form.login_role_name;
        payload.phone = normalizedPhone;
      }
      if (isEdit) {
        await staffAPI.update(id, payload);
        navigate(`/dashboard/staff/${id}`);
      } else {
        const res = await staffAPI.create(payload);
        const staffId = res?.data?.id;
        if (!staffId) {
          showError('Employee was saved but the response was incomplete. Check the staff list.');
          return;
        }
        navigate(`/dashboard/staff/${staffId}`, {
          state: {
            created: true,
            withAppAccess: Boolean(form.grant_app_access),
            phone: normalizedPhone || form.phone,
          },
        });
      }
    } catch (err) {
      const msg = err.message || 'Failed to save employee. Please try again.';
      const isPhoneConflict = /phone|already linked/i.test(msg);
      showError(
        msg,
        isPhoneConflict ? 'Use a different number, or uncheck “Grant app access” to save without login.' : ''
      );
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'ga-input w-full px-4 py-2 rounded-lg';
  const labelCls = 'block text-sm font-medium text-ga-text-muted mb-1';

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-2xl">
        <StaffTabs active="team" />

        <div className="flex items-center gap-4">
          <Link
            to={isEdit ? `/dashboard/staff/${id}` : '/dashboard/staff'}
            aria-label="Back"
            className="p-2 rounded-lg text-ga-text-primary hover:bg-ga-bg-surface-subtle"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-ga-text-primary">
              {isEdit ? 'Edit employee' : 'Add employee'}
            </h1>
            <p className="text-sm text-ga-text-muted mt-0.5">
              {isEdit ? 'Update profile and compensation details.' : 'Create a staff record and optionally enable phone OTP login.'}
            </p>
          </div>
        </div>

        {errorMsg && (
          <div ref={errorRef}>
            <AttentionStrip tone="danger" title="Could not save" message={errorMsg} />
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="ga-card rounded-xl shadow-sm border p-6 space-y-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ga-text-muted">Profile</h2>
            <div>
              <label className={labelCls}>Full name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className={inputCls}
                required
                autoFocus
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>
                  Mobile {form.grant_app_access && !isEdit ? '*' : ''}
                </label>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={form.phone}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, phone: e.target.value }));
                    if (phoneError) setPhoneError('');
                  }}
                  className={`${inputCls}${phoneError ? ' border-ga-danger' : ''}`}
                  placeholder="10-digit number"
                  maxLength={15}
                />
                {phoneError && (
                  <p className="mt-1 text-xs text-ga-danger">{phoneError}</p>
                )}
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className={inputCls}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Job role *</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                  className={inputCls}
                  required
                >
                  {ROLE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Joining date</label>
                <input
                  type="date"
                  value={form.joining_date}
                  onChange={(e) => setForm((f) => ({ ...f, joining_date: e.target.value }))}
                  className={inputCls}
                />
              </div>
            </div>
          </section>

          <section className="ga-card rounded-xl shadow-sm border p-6 space-y-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ga-text-muted">Compensation</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Salary type *</label>
                <select
                  value={form.salary_type}
                  onChange={(e) => setForm((f) => ({ ...f, salary_type: e.target.value }))}
                  className={inputCls}
                  required
                >
                  <option value="MONTHLY">Monthly</option>
                  <option value="DAILY">Daily</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Salary amount (₹)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.salary_amount}
                  onChange={(e) => setForm((f) => ({ ...f, salary_amount: e.target.value }))}
                  className={inputCls}
                />
              </div>
            </div>
          </section>

          {!isEdit && canGrantAppAccess && (
            <section className="ga-card rounded-xl shadow-sm border p-6 space-y-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-ga-text-muted flex items-center gap-2">
                <Smartphone className="h-4 w-4" />
                App access
              </h2>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.grant_app_access}
                  onChange={(e) => setForm((f) => ({ ...f, grant_app_access: e.target.checked }))}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm font-medium text-ga-text-primary">Grant app access now</span>
                  <span className="block text-xs text-ga-text-muted mt-0.5">
                    Creates a login linked to this employee. They sign in with phone OTP — no password needed.
                  </span>
                </span>
              </label>
              {form.grant_app_access && (
                <div className="space-y-3 pl-7">
                  <div>
                    <label className={labelCls}>Login role</label>
                    <select
                      value={form.login_role_name}
                      onChange={(e) => setForm((f) => ({ ...f, login_role_name: e.target.value }))}
                      className={inputCls}
                    >
                      {LOGIN_ROLE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <p className="text-xs text-ga-text-muted">
                    After creating, register their face for selfie check-in from the employee profile.
                  </p>
                </div>
              )}
            </section>
          )}

          {isEdit && (
            <section className="ga-card rounded-xl shadow-sm border p-6">
              <label className={labelCls}>Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                className={inputCls}
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </section>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={loading}
              className="ga-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              {loading ? 'Saving…' : (isEdit ? 'Save changes' : 'Create employee')}
            </button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(isEdit ? `/dashboard/staff/${id}` : '/dashboard/staff')}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
